import _extends from "@babel/runtime/helpers/extends";
import _orderBy from "lodash/orderBy";
import React from 'react';
import { Box, Text } from 'core/components';
import { Field, Select } from 'core/form';
import DashboardSelector from 'app/views/core/dashboards/DashboardSelector';
import { AGGREGATE_VIEWS, QUICK_VIEW_ROUTE } from 'app/views/core';
import { LandingSubOptionsSource } from 'app/stores/rbac/$rbac';

/**
 * Returns `{ disabled, disabledTooltip }` for a landing sub-option given the resource's label IDs.
 * Return `{ disabled: false }` (or omit the field) when the option should be enabled.
 */

/**
 * Returns quick-view select options sorted by label, derived from AGGREGATE_VIEWS.
 */
export function generateQuickViewOptions() {
  const options = AGGREGATE_VIEWS.map(view => ({
    value: view.path.replace(`${QUICK_VIEW_ROUTE}/`, ''),
    label: view.name
  }));
  return _orderBy(options, 'label');
}

/**
 * Builds the landing-page radio-group options array (with inline JSX sub-selects)
 * used by UserInfo and General forms.
 *
 * @param params.currentType     - Current value of the `defaultLandingPageType` field.
 * @param params.boardOptions    - Board select options (caller decides self vs assignable boards).
 * @param params.savedViewOptions - Saved-view select options.
 * @param params.hasMarketIntel  - When true, appends the "Market Intelligence" option.
 * @param params.menuWidth       - Optional width forwarded to sub-select menus (General uses 235).
 * @param params.valueFieldName  - Form field name for sub-select values. Defaults to 'defaultLandingPageValue'.
 */
export function buildLandingPageTypeOptions({
  currentType,
  boardOptions,
  savedViewOptions,
  hasMarketIntel = false,
  menuWidth,
  valueFieldName = 'defaultLandingPageValue',
  dashboardSelectorParams = {}
}) {
  const options = [{
    label: /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      as: "div"
    }, "Observation Decks"), currentType === 'observationDeck' && /*#__PURE__*/React.createElement(Field, {
      name: valueFieldName,
      options: _orderBy(boardOptions, 'label'),
      mt: 1
    }, /*#__PURE__*/React.createElement(Select, {
      showFilter: true,
      menuWidth: menuWidth
    }))),
    value: 'observationDeck'
  }, {
    label: 'NMS Dashboard',
    value: 'nmsDashboard'
  }, {
    label: 'Network Explorer',
    value: 'networkExplorer'
  }, {
    label: 'Library',
    value: 'library'
  }, {
    label: 'Synthetics',
    value: 'synthetics'
  }, {
    label: 'Data Explorer',
    value: 'explorer'
  }, {
    label: 'Alerting',
    value: 'alerting'
  }, {
    label: 'Insights',
    value: 'insights'
  }, {
    label: /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      as: "div"
    }, "Dashboards"), currentType === 'dashboard' && /*#__PURE__*/React.createElement(DashboardSelector, _extends({
      mt: 1,
      name: valueFieldName,
      menuWidth: menuWidth
    }, dashboardSelectorParams))),
    value: 'dashboard'
  }, {
    label: /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      as: "div"
    }, "Saved Views"), currentType === 'saved-view' && /*#__PURE__*/React.createElement(Field, {
      name: valueFieldName,
      options: _orderBy(savedViewOptions, 'label'),
      mt: 1
    }, /*#__PURE__*/React.createElement(Select, {
      showFilter: true,
      menuWidth: menuWidth
    }))),
    value: 'saved-view'
  }, {
    label: /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      as: "div"
    }, "Quick Views"), currentType === 'quick-view' && /*#__PURE__*/React.createElement(Field, {
      name: valueFieldName,
      options: generateQuickViewOptions(),
      mt: 1
    }, /*#__PURE__*/React.createElement(Select, {
      showFilter: true,
      menuWidth: menuWidth
    }))),
    value: 'quick-view'
  }];
  if (hasMarketIntel) {
    options.push({
      label: 'Market Intelligence',
      value: 'market-intel'
    });
  }
  return options;
}

/**
 * Builds a map of LandingSubOptionsSource keys to resolver thunks.
 * Each resolver produces the flat `{ label, value }` sub-option list for its landing type,
 * using compound values (e.g. "dashboard:42") that bulkUpdateUsers splits into
 * defaultLandingPageType + defaultLandingPageValue.
 *
 * Used by the Users admin table bulk-action landing-page dropdown.
 */
export function buildLandingPageSubOptionsResolvers({
  stores,
  disabledPropsGetters
}) {
  const {
    $boards,
    $dashboards,
    $savedViews
  } = stores;

  // Build Select sub-options for Boards
  const boardOptions = $boards.assignableBoards.map(model => {
    const label = model.name;
    const value = `observationDeck:${model.id}`;
    const disabledPropsGetter = disabledPropsGetters?.[LandingSubOptionsSource.BOARDS];
    const labelIds = model.labelIds ?? [];

    // Set disabled props or fallback to undefined based on the disabledPropsGetter function param
    const {
      disabled,
      disabledTooltip
    } = disabledPropsGetter ? disabledPropsGetter(labelIds) : {};
    return {
      label,
      value,
      disabled,
      disabledTooltip
    };
  });

  // Build Select sub-options for Dashboards
  const dashOptions = $dashboards.collection.get().map(model => {
    const label = model.get('dash_title');
    const value = `dashboard:${model.get('id')}`;
    const disabledPropsGetter = disabledPropsGetters?.[LandingSubOptionsSource.DASHBOARDS];
    const labelIds = model.labelIds ?? [];

    // Set disabled props or fallback to undefined based on the disabledPropsGetter function param
    const {
      disabled,
      disabledTooltip
    } = disabledPropsGetter ? disabledPropsGetter(labelIds) : {};
    return {
      label,
      value,
      disabled,
      disabledTooltip
    };
  });

  // Build Select sub-options for Saved Views
  const savedViewOptions = $savedViews.collection.get().map(model => {
    const label = model.get('view_name');
    const value = `saved-view:${model.id}`;
    const disabledPropsGetter = disabledPropsGetters?.[LandingSubOptionsSource.SAVED_VIEWS];
    const labelIds = model.labelIds ?? [];

    // Set disabled props or fallback to undefined based on the disabledPropsGetter function param
    const {
      disabled,
      disabledTooltip
    } = disabledPropsGetter ? disabledPropsGetter(labelIds) : {};
    return {
      label,
      value,
      disabled,
      disabledTooltip
    };
  });
  return {
    [LandingSubOptionsSource.BOARDS]: () => _orderBy(boardOptions, 'label'),
    [LandingSubOptionsSource.DASHBOARDS]: () => _orderBy(dashOptions, 'label'),
    [LandingSubOptionsSource.SAVED_VIEWS]: () => _orderBy(savedViewOptions, 'label'),
    [LandingSubOptionsSource.QUICK_VIEWS]: () => _orderBy(generateQuickViewOptions(), 'label')
  };
}

/**
 * Resolves `subOptionsSource` entries in a base options list into concrete `subOptions` arrays.
 * Returns a new array; options without a matching resolver are returned unchanged.
 *
 * Used by the Users admin table to build the landing-page bulk-action dropdown.
 */
export function resolveLandingPageOptions({
  baseOptions,
  resolvers
}) {
  return baseOptions.map(option => {
    if (option.subOptionsSource) {
      const resolver = resolvers[option.subOptionsSource];
      if (resolver) {
        const subOptions = resolver();
        if (subOptions.length > 0) {
          return {
            ...option,
            subOptions
          };
        }
      }
    }
    return option;
  });
}